import React from 'react';
import { EnvelopeIcon, QuestionMarkCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

// Format a signed delta with a real +/− sign (U+2212 for minus), avoiding the
// "+-3" bug that a hardcoded "+" prefix produces for negative values.
const signedFmt = new Intl.NumberFormat('en-US', { signDisplay: 'exceptZero' });
const formatChange = (n) => (typeof n === 'number' ? signedFmt.format(n) : '0');

/**
 * DailyInsights component displays daily metrics for the RAG chatbot system
 * including unread messages, open queries, and action items.
 */
const DailyInsights = ({ data }) => {
  // Guard against a missing key so a partial payload can't crash the dashboard.
  const {
    unreadMessages = { count: 0, change: 0 },
    openQueries = { count: 0, change: 0 },
    actionItems = { count: 0, highPriority: 0 },
  } = data || {};

  const insights = [
    {
      title: 'Unread Messages',
      value: unreadMessages.count,
      change: `${formatChange(unreadMessages.change)} since yesterday`,
      icon: <EnvelopeIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />,
      bgColor: 'bg-blue-900/20',
      textColor: 'text-blue-400',
    },
    {
      title: 'Open Queries',
      value: openQueries.count,
      change: `${formatChange(openQueries.change)} since yesterday`,
      icon: <QuestionMarkCircleIcon className="h-5 w-5 text-purple-400" aria-hidden="true" />,
      bgColor: 'bg-purple-900/20',
      textColor: 'text-purple-400',
    },
    {
      title: 'Action Items',
      value: actionItems.count,
      change: `${actionItems.highPriority} high priority`,
      icon: <ExclamationCircleIcon className="h-5 w-5 text-orange-400" aria-hidden="true" />,
      bgColor: 'bg-orange-900/20',
      textColor: 'text-orange-400',
    },
  ];

  return (
    <div className="sm-card p-6 h-full">
      <h2 className="text-lg font-semibold mb-4 text-white">Daily Insights</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {insights.map((insight, index) => (
          <div key={index} className={`${insight.bgColor} rounded-lg p-4`}>
            <div className="flex flex-col items-center text-center">
              <div className="p-2 rounded-full bg-gray-700 mb-2">
                {insight.icon}
              </div>
              <h3 className="text-sm font-medium text-gray-400">{insight.title}</h3>
              <p className="text-2xl font-bold text-white tabular-nums">{insight.value}</p>
              <p className="text-xs text-gray-400">{insight.change}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DailyInsights;
